import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getActiveFilters } from 'selectors/search';
import { setFilter } from 'actions/search';
import {
  phases,
  clinicalTrialStatuses,
  roles,
  sponsorsTypeahead,
  leadSponsorTypeahead,
} from 'services/inputTokens';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';
import { useAuth } from 'hooks';
import licenseType from 'models/licenseType';

import { Filter } from '../helpers';
import FilterGroup from './FilterGroup';
import { trialKeys } from './keys';

const Trials = ({ loading }) => {
  const dispatch = useDispatch();
  const activeFilters = useSelector(getActiveFilters(Object.keys(trialKeys)));
  const phaseFilterActive = activeFilters.includes('phase');
  const { hasLicense } = useAuth();

  const hasIndustryOnlyLicense =
    hasLicense([licenseType.industry], true) &&
    !hasLicense([licenseType.academic], true);

  const hasAcademicOnlyLicense =
    hasLicense([licenseType.academic], true) &&
    !hasLicense([licenseType.industry], true);

  // Remove filters that are not applicable based on the license type. This can occur when switching between users with different licenses.
  if (
    hasIndustryOnlyLicense &&
    (activeFilters.includes('academicMinSponsoredTrials') ||
      activeFilters.includes('minSponsoredTrials'))
  ) {
    dispatch(setFilter('minSponsoredTrials', ''));
    dispatch(setFilter('academicMinSponsoredTrials', ''));
  }

  if (
    hasAcademicOnlyLicense &&
    (activeFilters.includes('bpMinSponsoredTrials') ||
      activeFilters.includes('minSponsoredTrials'))
  ) {
    dispatch(setFilter('minSponsoredTrials', ''));
    dispatch(setFilter('bpMinSponsoredTrials', ''));
  }

  if (
    !hasAcademicOnlyLicense &&
    !hasIndustryOnlyLicense &&
    (activeFilters.includes('bpMinSponsoredTrials') ||
      activeFilters.includes('academicMinSponsoredTrials'))
  ) {
    dispatch(setFilter('academicMinSponsoredTrials', ''));
    dispatch(setFilter('bpMinSponsoredTrials', ''));
  }

  const getMinSponsorFilterKey = () => {
    if (hasIndustryOnlyLicense) return trialKeys.bpMinSponsoredTrials.key;
    if (hasAcademicOnlyLicense) return trialKeys.academicMinSponsoredTrials.key;

    return trialKeys.minSponsoredTrials.key;
  };

  return (
    <FilterGroup
      icon='trial'
      title='Clinical Trials'
      activeKeys={activeFilters}
      sectionKey='trials'
    >
      <>
        <Filter
          filterType='checkbox'
          filterId={trialKeys.hasClinicalTrials.key}
          disabled={loading}
          labelText='Listed on at least one clinical trial'
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Phase'
          options={phases}
          filterId={trialKeys.phase.key}
          disabled={loading}
        />
        <Filter
          disabled={!phaseFilterActive || loading}
          filterType='datePicker'
          placeholder={['Phase Start From', 'Phase Start To']}
          type='range'
          filterId={trialKeys.phaseStartDate.key}
          horizontalTitle='Phase Start Date'
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Status'
          options={clinicalTrialStatuses}
          filterId={trialKeys.overallStatus.key}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Lead Sponsor'
          filterId={trialKeys.leadSponsor.key}
          options={leadSponsorTypeahead}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='All Sponsors'
          filterId={trialKeys.sponsors.key}
          options={sponsorsTypeahead}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Role'
          options={roles}
          filterId={trialKeys.roles.key}
          disabled={loading}
        />
        <Filter
          filterType='datePicker'
          placeholder={['Trial Start From', 'Trial Start To']}
          type='range'
          filterId={trialKeys.startDate.key}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={getMinSponsorFilterKey()}
          placeholder='Min. Sponsored Trials'
          min={0}
          disabled={loading}
        />
      </>
    </FilterGroup>
  );
};

Trials.propTypes = {
  loading: PropTypes.bool,
};

export default Trials;
